import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import '../../style/UpdateProfileImagePage.css';

const UpdateProfileImagePage = () => {
    const { id } = useParams();
    const { t } = useTranslation();
    const [currentImage, setCurrentImage] = useState(null);
    const [newImage, setNewImage] = useState(null);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchPetData = async () => {
            try {
                const storedUser = JSON.parse(localStorage.getItem('user'));
                const token = storedUser?.token;

                if (!token) {
                    setError(t('error.noAuthToken'));
                    return;
                }

                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/pets/${id}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                const profileImage = response.data.images?.find((image) => image.tag === 'profile')?.url;
                setCurrentImage(profileImage || null);
            } catch (err) {
                setError(t('error.loadPetData'));
            }
        };

        fetchPetData();
    }, [id, t]);

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setNewImage(file);

            const reader = new FileReader();
            reader.onload = () => {
                setCurrentImage(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!newImage) {
            setError(t('error.selectImage'));
            return;
        }

        const formData = new FormData();
        formData.append('file', newImage);

        try {
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            if (!token) {
                setError(t('error.noAuthToken'));
                return;
            }

            const response = await axios.put(
                `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/pets/${id}/profile-image`,
                formData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.status === 200) {
                setSuccess(true);
                setTimeout(() => navigate(`/pet/${id}`), 2000);
            }
        } catch (error) {
            setError(error.response?.data?.message || t('error.uploadImage'));
        }
    };

    const handleCancel = () => {
        navigate(`/pet/${id}`);
    };

    const handleDeleteImage = async () => {
        setError(null);

        if (!currentImage) {
            setError(t('error.noImageToDelete'));
            return;
        }

        try {
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            if (!token) {
                setError(t('error.noAuthToken'));
                return;
            }

            const response = await axios.delete(
                `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/pets/${id}/profile-image`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            if (response.status === 200) {
                setCurrentImage(null);
                setSuccess(true);
                navigate(`/pet/${id}`);
            }
        } catch (error) {
            setError(error.response?.data?.message || t('error.deleteImage'));
        }
    };

    return (
        <div className="update-profile-image-page">
            <div className="update-profile-image-header">
                <h1>{t('updateProfileImagePage.title')}</h1>
            </div>
            {error && <p className="error-message">{error}</p>}
            {success ? (
                <p className="success-message">{t('updateProfileImagePage.successMessage')}</p>
            ) : (
                <>
                    {currentImage && (
                        <>
                            <img
                                src={currentImage}
                                alt={t('updateProfileImagePage.currentImageAlt')}
                                className="current-image"
                            />
                            <button
                                type="button"
                                onClick={handleDeleteImage}
                                className="button button-danger"
                            >
                                {t('updateProfileImagePage.deleteButton')}
                            </button>
                        </>
                    )}
                    <form onSubmit={handleSubmit} className="update-profile-form">
                        <div>
                            <label htmlFor="newImage">{t('updateProfileImagePage.selectNewImage')}</label>
                            <input
                                type="file"
                                id="newImage"
                                accept="image/*"
                                onChange={handleImageChange}
                            />
                        </div>
                        <button type="submit" className="button button-primary">
                            {t('updateProfileImagePage.updateButton')}
                        </button>
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="button button-secondary"
                        >
                            {t('updateProfileImagePage.cancelButton')}
                        </button>
                    </form>
                </>
            )}
        </div>
    );
};

export default UpdateProfileImagePage;
